import { useEffect, useState } from "react";
import { Building2, MapPin, Briefcase, ArrowLeft, Calendar, DollarSign, CheckCircle, Loader2 } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { getJobDetails } from "../../API/getters";

export default function JobDetailsPage() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchJob() {
            try {
                setLoading(true);
                const data = await getJobDetails(id);

                const jobProcessed = {
                    ...data,
                    responsibilities: data.responsibilities
                        ? data.responsibilities.split("|").map(s => s.trim()).filter(s => s)
                        : [],
                    requirements: data.requirements
                        ? data.requirements.split("|").map(s => s.trim()).filter(s => s)
                        : [],
                    benefits: data.benefits
                        ? data.benefits.split("|").map(s => s.trim()).filter(s => s)
                        : [],
                };

                setJob(jobProcessed);
                setError(null);
            } catch (error) {
                console.error("Erro ao buscar vaga:", error);
                setError("Não foi possível carregar os detalhes da vaga.");
            } finally {
                setLoading(false);
            }
        }

        fetchJob();
    }, [id]);

    if (loading) {
        return (
            <div className="w-full py-6">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
                    <span className="text-gray-400 text-sm">Carregando vaga...</span>
                </div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="w-full py-6">
                <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
                    <p className="text-gray-600 mb-4">{error || 'Vaga não encontrada.'}</p>
                    <Link to="/vagas" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Voltar para vagas
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full py-6">
            {/* Navigation */}
            <div className="mb-8">
                <Link
                    to="/vagas"
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar para Vagas
                </Link>
            </div>

            {/* Main Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-6 md:p-8 border-b border-gray-100">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                        <div className="flex-shrink-0">
                            
                        </div>
                        <div className="flex-1">
                            <h1 className="text-2xl md:text-3xl font-light text-gray-800 tracking-tight mb-3">
                                {job.title}
                            </h1>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-gray-400" />
                                    <span>{job.company}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span>{job.location}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Briefcase className="w-4 h-4 text-gray-400" />
                                    <span>{job.contract_type}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 md:p-8 space-y-6">
                    {/* Description */}
                    <div>
                        <h2 className="text-base font-medium text-gray-800 mb-3">Descrição da Vaga</h2>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            {job.description}
                        </p>
                    </div>

                    {/* Responsibilities */}
                    {job.responsibilities.length > 0 && (
                        <div>
                            <h2 className="text-base font-medium text-gray-800 mb-3">Responsabilidades</h2>
                            <ul className="space-y-2">
                                {job.responsibilities.map((item, index) => (
                                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Requirements */}
                    {job.requirements.length > 0 && (
                        <div>
                            <h2 className="text-base font-medium text-gray-800 mb-3">Requisitos</h2>
                            <ul className="space-y-2">
                                {job.requirements.map((item, index) => (
                                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Benefits */}
                    {job.benefits.length > 0 && (
                        <div>
                            <h2 className="text-base font-medium text-gray-800 mb-3">Benefícios</h2>
                            <ul className="space-y-2">
                                {job.benefits.map((item, index) => (
                                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}